/**
 * QorTrace · Landing — Iter237
 *
 * Block 2 — Identify → Score → Migrate vertical timeline. Replaces the
 * 3-up horizontal strip with an asymmetric scrolling timeline that
 * has a continuous cyan connector spine. Steps zig-zag left/right on
 * desktop, stack vertically on mobile.
 *
 * No external animation library — pure CSS + scroll-driven
 * IntersectionObserver reveal.
 */
import React, { useEffect, useRef } from "react";


const STEPS = [
  {
    n: "01", t: "Identify",
    d: "Select a chain. Paste a wallet, contract, or fetch by address. Live on-chain data is pulled and normalized.",
    side: "left",
  },
  {
    n: "02", t: "Score",
    d: "Deterministic, evidence-grounded methodology — every point traceable to NIST-aligned cryptographic facts.",
    side: "right",
  },
  {
    n: "03", t: "Migrate",
    d: "Plain-English risk narrative + a versioned migration playbook tailored to the asset profile.",
    side: "left",
  },
];


export default function ProcessTimeline() {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add("qt-process-step--in");
            obs.unobserve(e.target);
          }
        }
      },
      { threshold: 0.2, rootMargin: "0px 0px -10% 0px" },
    );
    root.querySelectorAll(".qt-process-step").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={rootRef}
      className="qt-process-timeline relative"
      data-testid="process-timeline"
    >
      <style>{`
        .qt-process-timeline {
          padding: 40px 0 20px;
          position: relative;
        }
        /* The connector spine — runs full-height of the section. */
        .qt-process-timeline::before {
          content: "";
          position: absolute;
          left: 50%;
          top: 80px;
          bottom: 40px;
          width: 1px;
          background:
            linear-gradient(
              to bottom,
              rgba(0,229,255,0)   0%,
              rgba(0,229,255,0.6) 8%,
              rgba(0,229,255,0.6) 92%,
              rgba(0,229,255,0)   100%
            );
          transform: translateX(-0.5px);
          pointer-events: none;
        }
        @media (max-width: 768px) {
          .qt-process-timeline::before {
            left: 16px;
            transform: none;
          }
        }
        .qt-process-step {
          position: relative;
          margin: 60px 0;
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 600ms ease, transform 600ms ease;
        }
        .qt-process-step--in {
          opacity: 1;
          transform: translateY(0);
        }
        /* The node on the spine. */
        .qt-process-step__node {
          position: absolute;
          left: 50%;
          top: 8px;
          width: 12px; height: 12px;
          border-radius: 50%;
          background: #00E5FF;
          box-shadow: 0 0 0 4px rgba(0,229,255,0.15),
                      0 0 16px rgba(0,229,255,0.5);
          transform: translateX(-50%);
        }
        @media (max-width: 768px) {
          .qt-process-step__node {
            left: 16px;
            transform: translateX(-50%);
          }
        }
        /* The numeric watermark sitting behind the content. */
        .qt-process-step__numbg {
          position: absolute;
          font-family: "Chivo", sans-serif;
          font-weight: 900;
          font-size: 120px;
          line-height: 1;
          letter-spacing: -0.04em;
          color: transparent;
          -webkit-text-stroke: 1px rgba(0,229,255,0.18);
          pointer-events: none;
          user-select: none;
          top: -28px;
        }
        .qt-process-step__card {
          width: 46%;
          padding: 24px 28px;
          background: rgba(11,19,36,0.6);
          border-left: 2px solid rgba(31,221,131,0.40);
          border-radius: 0 6px 6px 0;
        }
        .qt-process-step--left  .qt-process-step__card  { margin-right: auto; }
        .qt-process-step--left  .qt-process-step__numbg { right: 52%; }
        .qt-process-step--right .qt-process-step__card  { margin-left: auto; }
        .qt-process-step--right .qt-process-step__numbg { left: 52%; }
        @media (max-width: 768px) {
          .qt-process-step__card {
            width: auto;
            margin-left: 40px !important;
            margin-right: 0 !important;
          }
          .qt-process-step__numbg {
            position: static;
            font-size: 72px;
            display: block;
            margin-bottom: 8px;
          }
        }
      `}</style>

      {STEPS.map((s) => (
        <div
          key={s.n}
          className={`qt-process-step qt-process-step--${s.side}`}
          data-testid={`process-step-${s.n}`}
        >
          <span className="qt-process-step__node" />
          <span className="qt-process-step__numbg">{s.n}</span>
          <div className="qt-process-step__card">
            <div
              className="qt-mono"
              style={{
                fontSize: 10, color: "#00E5FF",
                letterSpacing: "0.20em", textTransform: "uppercase",
                fontWeight: 700, marginBottom: 6,
              }}
            >
              STEP {s.n}
            </div>
            <h3
              className="qt-h2"
              style={{ fontSize: 28, lineHeight: 1.15, color: "#fff" }}
            >
              {s.t}
            </h3>
            <p
              className="text-sm"
              style={{
                color: "var(--qt-text-2)", marginTop: 10,
                lineHeight: 1.65,
              }}
            >
              {s.d}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
